import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { BRAND } from "../data";
import { useBooking } from "../BookingContext";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Training", href: "#pillars" },
  { label: "Coaching", href: "#coaching" },
  { label: "Pricing", href: "#pricing" },
  { label: "Clean Energy", href: "#clean-energy" },
  { label: "Books", href: "#books" },
  { label: "Me vs Me", href: "#mevsme" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    const wasMobile = mobile;
    setMobile(false);
    const target = document.querySelector(href);
    if (!target) return;
    const doScroll = () => target.scrollIntoView({ behavior: "smooth", block: "start" });
    // On mobile, wait for the menu collapse animation so layout is settled before scrolling.
    if (wasMobile) setTimeout(doScroll, 380);
    else doScroll();
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050A15]/90 backdrop-blur-md border-b border-[#1D2847]" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" onClick={(e) => handleNav(e, "#top")} className="flex items-center gap-3">
          <img src={BRAND.logo} alt="On This Rock LLC" className="h-12 w-auto" />
          <span className="hidden sm:block font-display font-extrabold tracking-tight text-lg leading-none">
            ON THIS ROCK <span className="text-[#A3A8B8] text-xs align-top">LLC</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNav(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className="text-sm font-medium text-[#A3A8B8] hover:text-[#D4AF37] transition-colors uppercase tracking-wider"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-btn"
            onClick={() => openBooking("")}
            className="hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider text-sm px-5 py-3 transition-colors"
          >
            <CalendarCheck size={16} /> Book a Live Session
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobile((v) => !v)}
            className="lg:hidden text-[#F8F9FA] p-2"
            aria-label="Menu"
          >
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0C142A] border-t border-[#1D2847]"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={(e) => handleNav(e, l.href)}
                  className="py-3 text-[#A3A8B8] hover:text-[#D4AF37] uppercase tracking-wider text-sm border-b border-[#1D2847]"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setMobile(false); openBooking(""); }}
                className="mt-4 bg-[#D4AF37] text-[#050A15] font-bold uppercase tracking-wider text-sm px-5 py-3"
              >
                Book a Live Session
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
